import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";
import { getMountainDate } from "../utils/formatWorkout";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
};

const btn = (bg, color, disabled) => ({
    padding: "7px 16px",
    background: disabled ? "#2a2a4a" : bg,
    color: disabled ? "#555" : color,
    border: "none",
    borderRadius: "5px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "13px",
    fontWeight: "bold",
});

const SPORT_LABEL = { bike: "Bike · watts / ERG", run: "Run · pace" };

// Loose client-side mirror of the backend's sportFor(), used only to grey out the
// Generate button before a call is made — the backend's own mapping is authoritative
// and reports anything it can't plan back as `skipped`.
const looksPlannable = (type) => /run|bike|ride|cycle|spin|trainer/i.test(type || "");

const shiftDate = (date, days) => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
};

const downloadJson = (filename, data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const formatStep = (step) => {
    // A run step is measured in seconds or in miles.
    const length = step.miles != null ? `${step.miles} mi` : `${Math.round((step.seconds / 60) * 10) / 10} min`;
    const target = step.watts != null
        ? `${step.watts}${step.toWatts != null ? `→${step.toWatts}` : ""} W`
        : `${step.pace}${step.toPace ? `→${step.toPace}` : ""} /mi`;
    const free = step.erg === false ? " (free ride)" : "";
    return `${step.name ? `${step.name}: ` : ""}${length} @ ${target}${free}`;
};

const WorkoutPlanExport = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(getMountainDate());
    const [workout, setWorkout] = useState(null);
    const [loadingDay, setLoadingDay] = useState(true);
    const [dayError, setDayError] = useState("");
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState("");
    const [gen, setGen] = useState(null);

    const authHeaders = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return { Authorization: `Bearer ${authgear.accessToken}` };
    }, []);

    const loadDay = useCallback(async (d) => {
        setLoadingDay(true);
        setDayError("");
        setGen(null);
        setGenError("");
        try {
            const { data } = await axios.get(`${base_url}/api/workouts/daily/${d}`);
            setWorkout(data.success ? data.workout : null);
        } catch (err) {
            setDayError(err.response?.data?.message || err.message);
        } finally {
            setLoadingDay(false);
        }
    }, []);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        loadDay(date);
    }, [navigate, loadDay, date]);

    // Explicit button click, not automatic on load or date change — this calls the
    // Anthropic API and shouldn't fire just from browsing days.
    const handleGenerate = async () => {
        setGenerating(true);
        setGenError("");
        try {
            const headers = await authHeaders();
            const { data } = await axios.post(`${base_url}/workout-plan/${date}/generate`, {}, { headers });
            if (data.success) {
                setGen(data);
            } else {
                setGenError(data.message || "No workout found for this date");
            }
        } catch (err) {
            setGenError(err.response?.data?.message || err.message);
        } finally {
            setGenerating(false);
        }
    };

    const eligibleCount = workout
        ? workout.activities.filter(a => looksPlannable(a.type)).length
        : 0;

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 40px", maxWidth: "760px", margin: "0 auto" }}>
                <h2 style={{ color: "#00d4ff", margin: "0 0 6px" }}>Workout Plan JSON</h2>
                <p style={{ color: "#888", fontSize: "13px", marginTop: 0, marginBottom: "20px" }}>
                    Turns the day's planned Run or Bike activity into a structured interval file for the
                    KICKR Telemetry app's Workout ▾ menu.
                </p>

                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
                    <button onClick={() => setDate(shiftDate(date, -1))} style={btn("transparent", "#888")}>← Prev</button>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: "5px", color: "#ddd", padding: "6px 10px", fontSize: "13px" }}
                    />
                    <button onClick={() => setDate(shiftDate(date, 1))} style={btn("transparent", "#888")}>Next →</button>
                    {date !== getMountainDate() && (
                        <button onClick={() => setDate(getMountainDate())} style={btn("transparent", "#00d4ff")}>Today</button>
                    )}
                </div>

                {loadingDay && <div style={{ ...card, fontSize: "13px", color: "#555" }}>Loading…</div>}
                {!loadingDay && dayError && (
                    <div style={{ ...card, borderColor: "#4a2a2a", color: "#f88", fontSize: "13px" }}>{dayError}</div>
                )}
                {!loadingDay && !dayError && !workout && (
                    <div style={{ ...card, fontSize: "13px", color: "#555" }}>No workout planned for this date.</div>
                )}

                {!loadingDay && workout && (
                    <div style={{ ...card, marginBottom: "16px" }}>
                        <div style={{ fontSize: "15px", fontWeight: "bold", color: "#ddd" }}>{workout.planName}</div>
                        {workout.description && (
                            <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>{workout.description}</div>
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "12px" }}>
                            {workout.activities.map((a, i) => (
                                <div key={i} style={{ fontSize: "12px", color: "#ccc", background: "#111128", borderRadius: "4px", padding: "8px" }}>
                                    <span style={{ color: "#00d4ff", fontWeight: "bold" }}>{a.type}</span>
                                    {a.duration ? ` · ${a.duration}` : ""}
                                    {a.distance ? ` · ${a.distance}` : ""}
                                    {a.pace ? ` · ${a.pace}` : ""}
                                    {a.description ? <div style={{ marginTop: "4px", color: "#999" }}>{a.description}</div> : null}
                                    {a.notes ? <div style={{ marginTop: "2px", color: "#666" }}>{a.notes}</div> : null}
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={handleGenerate}
                            disabled={generating || eligibleCount === 0}
                            style={{ ...btn("#00d4ff", "#0d0d1a", generating || eligibleCount === 0), marginTop: "14px" }}
                        >
                            {generating ? "Generating…" : eligibleCount === 0 ? "No Run/Bike activity to convert" : "Generate Workout JSON"}
                        </button>
                    </div>
                )}

                {genError && (
                    <div style={{ ...card, borderColor: "#4a2a2a", color: "#f88", fontSize: "13px", marginBottom: "16px" }}>{genError}</div>
                )}

                {gen && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {gen.results.map((r) => (
                            <div key={r.index} style={card}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                                    <div style={{ fontSize: "14px", fontWeight: "bold", color: "#ddd" }}>{r.plan.name}</div>
                                    <div style={{ fontSize: "11px", color: "#888" }}>{SPORT_LABEL[r.plan.sport]}</div>
                                </div>
                                <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
                                    From: {r.sourceType}{r.sourceDescription ? ` — ${r.sourceDescription}` : ""}
                                </div>
                                <div style={{ marginTop: "10px", fontSize: "12px", color: "#ccc", background: "#111128", borderRadius: "4px", padding: "8px", display: "flex", flexDirection: "column", gap: "3px" }}>
                                    {r.plan.steps.map((s, i) => <div key={i}>{formatStep(s)}</div>)}
                                </div>
                                <button
                                    onClick={() => downloadJson(`${date}-${r.plan.sport}-${r.index}.json`, r.plan)}
                                    style={{ ...btn("#22c55e", "#0d0d1a"), marginTop: "12px" }}
                                >
                                    Download JSON
                                </button>
                            </div>
                        ))}

                        {gen.errors.map((e) => (
                            <div key={`err-${e.index}`} style={{ ...card, borderColor: "#4a2a2a", fontSize: "12px", color: "#f88" }}>
                                Could not generate a plan for {e.sourceType}: {e.message}
                            </div>
                        ))}

                        {gen.skipped.map((s) => (
                            <div key={`skip-${s.index}`} style={{ ...card, fontSize: "12px", color: "#666" }}>
                                Skipped {s.sourceType}: {s.reason}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkoutPlanExport;
